'use client';
// Public login. A branded split layout: an InnHealth brand panel (left, ≥lg) alongside
// the app-kit LoginForm (right), which owns validation + the error banner — we only supply
// onSubmit + copy. Dev-only quick-login buttons sign in as each seeded demo role.
import { LoginForm } from '@santiagossz/app-kit/blocks';
import { useRouter } from 'next/navigation';
import { signIn } from '@/auth/auth.actions';
import { LogoMark, Wordmark } from '@/components/brand/Logo';

const DEMO_PASSWORD = 'Demo!2026';
const QUICK_LOGINS = [
  { label: 'Super Admin', email: 'super@innhealth.co' },
  { label: 'Client Admin', email: 'admin@demo.innhealth.co' },
  { label: 'Docente', email: 'docente@demo.innhealth.co' },
  { label: 'Estudiante', email: 'est1@demo.innhealth.co' },
];

export default function LoginPage() {
  const router = useRouter();
  const go = async (v: { email: string; password: string }) => {
    await signIn(v);
    router.replace('/');
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      {/* Brand panel — the identity, shown on wide screens. */}
      <aside
        className="relative hidden overflow-hidden p-12 text-white lg:flex lg:flex-col lg:justify-between"
        style={{
          background:
            'radial-gradient(120% 90% at 15% 0%, #0e8296 0%, #0a6478 40%, #084a5c 100%)',
        }}
      >
        {/* Ambient heartbeat motif */}
        <svg
          viewBox="0 0 600 160"
          fill="none"
          aria-hidden
          preserveAspectRatio="xMidYMid slice"
          className="pointer-events-none absolute inset-x-0 bottom-24 h-40 w-full text-white/[0.07]"
        >
          <path
            d="M0 80H150L172 80L184 34L204 126L218 80H360L382 80L394 34L414 126L428 80H600"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        <div className="relative flex items-center gap-3">
          <LogoMark size={40} />
          <span className="font-display text-xl font-semibold tracking-tight">
            InnHealth
          </span>
        </div>

        <div className="relative max-w-md space-y-5">
          <h1 className="font-display text-[2.6rem] font-semibold leading-[1.05] tracking-tight">
            Formación clínica de alto impacto para cada equipo de salud.
          </h1>
          <p className="text-base leading-relaxed text-white/70">
            Cursos, evaluaciones y certificados en un solo portal — construido
            para hospitales y las personas que los enseñan.
          </p>
        </div>

        <p className="relative text-sm text-white/50">
          Portal Educativo · CIEMP
        </p>
      </aside>

      {/* Auth panel */}
      <main className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm space-y-6">
          <div className="flex justify-center lg:hidden">
            <Wordmark />
          </div>

          <LoginForm
            onSubmit={go}
            title="Inicia sesión"
            subtitle="Accede con tu correo institucional."
          />

          {process.env.NODE_ENV !== 'production' && (
            <div className="space-y-2">
              <p className="text-center text-xs text-muted-foreground">
                Acceso rápido (demo)
              </p>
              <div className="grid grid-cols-2 gap-2">
                {QUICK_LOGINS.map((u) => (
                  <button
                    key={u.email}
                    type="button"
                    onClick={() => go({ email: u.email, password: DEMO_PASSWORD })}
                    className="rounded-md border px-2 py-1.5 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:bg-accent hover:text-accent-foreground"
                  >
                    {u.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
